import Tooltip from "@components/ui/tooltip";
import { useOthersMapped, useSelf } from "@liveblocks/react";
import {
  ActiveEntityProvider,
  BroadcastProvider,
  CollaborationEnabledProvider,
} from "../providers";
import { Suspense } from "./Suspense";

type TUser = {
  name: string;
  color: string;
  email: string;
  avatar?: string;
  theme?: "dark" | "light";
};

const MAX_OTHERS = 2;

export const Avatars = () => {
  return (
    <CollaborationEnabledProvider>
      <Suspense feature="Avatars">
        <BroadcastProvider>
          <ActiveEntityProvider>
            <Wrapper />
          </ActiveEntityProvider>
        </BroadcastProvider>
      </Suspense>
    </CollaborationEnabledProvider>
  );
};

const Wrapper = () => {
  const me = useSelf();
  const others = useOthersMapped((other) => other.info);
  const reversed = others.slice().reverse();
  const hasMoreUsers = others.length > MAX_OTHERS;
  const visibleOthers = hasMoreUsers ? reversed.slice(0, MAX_OTHERS) : reversed;
  const hiddenOthers = hasMoreUsers ? reversed.slice(MAX_OTHERS) : [];
  return (
    <div className="tw-mr-2 tw-flex tw-items-center">
      {!!hiddenOthers.length && <HiddenAvatars hiddenOthers={hiddenOthers} />}
      {me && (
        <Avatar
          user={me.info as TUser}
          index={0}
          length={visibleOthers.length}
          isMe
        />
      )}
      {visibleOthers.map(([key, user], index) => (
        <Avatar
          key={key}
          user={user as TUser}
          index={index + 1}
          length={visibleOthers.length}
        />
      ))}
    </div>
  );
};

const Avatar = ({
  user,
  index,
  length,
  isMe,
}: {
  user: TUser;
  index: number;
  length: number;
  isMe?: boolean;
}) => {
  return (
    <div className="-tw-mr-2" style={{ zIndex: length - index }}>
      <Tooltip placement="bottom">
        <Tooltip.Trigger>
          <div className="tw-flex tw-size-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-white">
            <div
              style={{ backgroundColor: user.color }}
              className="tw-flex tw-size-6 tw-cursor-default tw-items-center tw-justify-center tw-rounded-full"
            >
              <span
                className="tw-font-sans tw-text-[9px] tw-font-medium tw-uppercase"
                style={{
                  color: user.theme === "dark" ? "#fff" : "#000",
                }}
              >
                {getInitials(user.name)}
              </span>
            </div>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Content>
          <Tooltip.Text>{user.name}</Tooltip.Text>
        </Tooltip.Content>
      </Tooltip>
    </div>
  );
};

const HiddenAvatars = ({ hiddenOthers }: { hiddenOthers: any }) => {
  return (
    <div className="-tw-mr-2" style={{ zIndex: 4 }}>
      <Tooltip placement="bottom">
        <Tooltip.Trigger>
          <div className="tw-flex tw-size-7 tw-cursor-default tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-[transparent] tw-bg-white">
            <div className="tw-flex tw-size-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-200 tw-text-center tw-font-sans tw-text-[9px]/normal tw-font-medium tw-text-blue-600">
              +{hiddenOthers.length}
            </div>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Content>
          <Tooltip.Text className="tw-py-2">
            <HiddenAvatarsTooltip hiddenOthers={hiddenOthers} />
          </Tooltip.Text>
        </Tooltip.Content>
      </Tooltip>
    </div>
  );
};

const HiddenAvatarsTooltip = ({ hiddenOthers }: { hiddenOthers: any }) => {
  return (
    <div className="tw-space-y-2">
      {hiddenOthers.map(([key, user]: any) => (
        <div className="tw-flex tw-items-center" key={key}>
          <div
            className="tw-mr-1 tw-flex tw-size-6 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-bg-blue-base"
            style={{ borderColor: user.color }}
          >
            <div className="tw-flex tw-size-5 tw-cursor-default tw-items-center tw-justify-center tw-rounded-full tw-bg-white">
              <span className="tw-font-sans tw-text-[9px] tw-font-medium tw-uppercase tw-text-blue-base">
                {getInitials(user.name)}
              </span>
            </div>
          </div>
          <span className="tw-font-sans tw-text-[11px]/tight tw-font-semibold tw-text-white">
            {user.name}
          </span>
        </div>
      ))}
    </div>
  );
};

const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2);
};
